/**
 * React hooks around the locale-prefixed help URL scheme (see ../locales.ts).
 */
import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router';
import articlesData from '../articles.json';
import {
  HELP_CONTENT_LOCALES,
  HELP_URL_LOCALES,
  HelpUrlLocale,
  helpBasePath,
  helpPathForLocale,
  parseHelpLocaleFromPath,
  splitHelpPath,
} from '../locales';
import {Article} from '../types';

const allArticles = articlesData as Article[];

/** Locale prefix of the current URL, or undefined on bare (English) URLs. */
export function useHelpUrlLocale(): HelpUrlLocale | undefined {
  const {pathname} = useLocation();
  return parseHelpLocaleFromPath(pathname);
}

/**
 * The locale whose content the current help URL serves. The URL — never the
 * UI language — decides this, so a canonical URL always serves one language:
 * bare `/help/*` URLs are English, prefixed URLs are their locale.
 */
export function useHelpContentLocale(): string {
  return useHelpUrlLocale() ?? 'en';
}

/** Base path for links within the current help locale ('/help' | '/help/fr'). */
export function useHelpBasePath(): string {
  return helpBasePath(useHelpUrlLocale());
}

export type HreflangAlternate = {hrefLang: string; href: string};

/**
 * Bidirectional hreflang cluster for the current help page: one alternate per
 * locale that actually has the article (all five for landing/category pages),
 * plus `x-default` pointing at English. Every locale version of a page emits
 * the identical cluster, which is what makes it bidirectional.
 */
export function useHelpAlternates(slug?: string): HreflangAlternate[] {
  const {pathname} = useLocation();
  const origin = import.meta.env.VITE_FRONTEND_URL;
  const {suffix} = splitHelpPath(pathname);

  const locales = slug
    ? HELP_CONTENT_LOCALES.filter((locale) =>
        allArticles.some((a) => a.slug === slug && a.locale === locale),
      )
    : [...HELP_CONTENT_LOCALES];

  const alternates: HreflangAlternate[] = locales.map((locale) => ({
    hrefLang: locale,
    href: `${origin}${helpBasePath(locale)}${suffix}`,
  }));
  alternates.push({
    hrefLang: 'x-default',
    href: `${origin}${helpBasePath('en')}${suffix}`,
  });
  return alternates;
}

// One auto-redirect per SPA session: deep links (mobile app, search results)
// land localized, but an explicit later choice of English in the help language
// switcher is respected instead of bouncing straight back.
let autoRedirectDone = false;

/** Test-only reset for the once-per-session auto-redirect guard. */
export function resetHelpAutoRedirectForTests(): void {
  autoRedirectDone = false;
}

/**
 * On the first help-center visit of the session, send visitors whose UI
 * language has a help translation from the bare (English) URLs to their
 * locale's URLs, preserving query (`?platform=mobile`) and hash deep links.
 * Returns the redirect target, or null when no redirect should happen —
 * crawlers and the prerenderer run with the default (en) language and are
 * never redirected.
 */
export function useHelpLocaleAutoRedirect(): string | null {
  const {pathname, search, hash} = useLocation();
  const {locale} = useIntl();

  // Computed once per layout mount; the module flag makes it once per session.
  const [redirect, setRedirect] = useState<{
    from: string;
    target: string | null;
  }>(() => {
    const from = pathname;
    if (autoRedirectDone) {
      return {from, target: null};
    }
    autoRedirectDone = true;
    if (parseHelpLocaleFromPath(pathname)) {
      return {from, target: null};
    }
    const base = locale.split('-')[0];
    if (!(HELP_URL_LOCALES as readonly string[]).includes(base)) {
      return {from, target: null};
    }
    return {
      from,
      target: `${helpPathForLocale(pathname, base)}${search}${hash}`,
    };
  });

  // Retire the target as soon as we have moved. `/help` is a single layout
  // route covering both the bare and the locale-prefixed URLs, so the layout
  // stays mounted across the redirect: a target that outlived the navigation
  // would keep rendering <Navigate> in place of the page, leaving the help
  // center blank until a hard refresh (the mobile deep links always land on a
  // bare URL, so they hit this every time). Retiring on the first navigation —
  // rather than on "does the URL carry a prefix yet" — is also what lets a
  // later English pick in the switcher stay put instead of bouncing back.
  // This is react.dev's adjust-state-while-rendering pattern: React re-runs
  // the render immediately, with nothing committed in between.
  if (redirect.target !== null && redirect.from !== pathname) {
    setRedirect({from: pathname, target: null});
  }

  return redirect.target;
}
